import { useSuspenseQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { useEnableGcpAccount } from "featureFlags/hooks/useEnableGcpAccount";
import {
  ResponseWorkspaceEngine,
  WorkspaceEngine,
  WorkspaceEngineStatus,
} from "services/engines/engine.types";
import { systemEngineEnvironment } from "services/environment/systemEngine";
import { getFireboltCoreConfig } from "services/liteRuntimeConfig";

import { SYSTEM_ENGINE } from "pages/DevelopWorkspace/workspace.constants";

import { useCurrentAccount } from "components/Account/useCurrentAccount";

import { ReactQueryKeysAccount } from "../queryKeys";

interface Options {
  includeSystemEngine?: boolean;
  isFirstUser?: boolean;
  accountName?: string;
  byOwnerUsername?: string;
}

export const fetchWorkspaceEngines = async (options?: Options) => {
  if (process.env.REACT_APP_LITE) {
    // Firebolt Core serves a single engine over the local /query proxy and
    // does not expose information_schema.engines; querying it fails the
    // whole workspace at startup. Synthesize the one-engine list from the
    // runtime config instead — the same value that seeds the default
    // execution context, so the workspace's engine lookup always resolves.
    return [
      {
        ...SYSTEM_ENGINE,
        engineName: getFireboltCoreConfig().defaultEngine,
        status: WorkspaceEngineStatus.RUNNING,
        // The workspace query helpers parse `https://${engine.url}` before
        // buildEngineUrl's lite branch rewrites the request to
        // window.location + /query, so the value must be a parseable
        // host/path — an empty url throws "Failed to construct 'URL'".
        url: `${window.location.host}/query`,
      },
    ];
  }

  const where = options?.byOwnerUsername
    ? `WHERE engine_owner = '${options.byOwnerUsername}'`
    : "";

  const result = await systemEngineEnvironment.execute<ResponseWorkspaceEngine>(
    `select * from information_schema.engines ${where} order by lower(engine_name);`,
    { accountName: options?.accountName }
  );
  const [response] = result;
  return response.rows.map(r => ({
    ...r,
    nodes: +r.nodes,
    clusters: +r.clusters,
    minClusters: +r.minClusters,
    maxClusters: +r.maxClusters,
  }));
};

export const useWorkspaceEngines = (options?: Options) => {
  const enabledGcpAccount = useEnableGcpAccount();
  const [dataLength, setDataLength] = useState(0);
  const includeSystemEngine = options?.includeSystemEngine;
  const isFirstUser = options?.isFirstUser;
  const { getAccount } = useCurrentAccount();

  const account = getAccount();

  const queryKey: string[] = [
    ReactQueryKeysAccount.workspaceEngines,
    options?.accountName ?? account.accountName,
  ];

  if (isFirstUser) {
    queryKey.push("FIRST_USER");
  }

  if (includeSystemEngine) {
    queryKey.push("INCLUDE_SYSTEM_ENGINE");
  }

  if (options?.byOwnerUsername) {
    queryKey.push(`BY_OWNER-${options.byOwnerUsername}`);
  }

  const { data, isLoading, error } = useSuspenseQuery<WorkspaceEngine[], Error>(
    {
      queryKey,
      queryFn: () => fetchWorkspaceEngines(options),
      refetchInterval: isFirstUser && !dataLength ? 2000 : false,
    }
  );

  useEffect(() => {
    setDataLength(data.length);
  }, [data.length]);

  const engines = useMemo(() => {
    const engines = data ? [...data] : undefined;

    if (includeSystemEngine && engines) {
      const account = getAccount();

      if (!account) {
        throw new Error("Account is not defined");
      }

      // todo: enabledGcpAccount is just a temporary flag
      if (!process.env.REACT_APP_LITE && !enabledGcpAccount) {
        const systemEngineUrl =
          systemEngineEnvironment.getApiEndpoint().replace(/^https?:\/\//, "") +
          "/query";

        engines.unshift({
          ...SYSTEM_ENGINE,
          url: systemEngineUrl,
        });
      }
    }

    return engines;
  }, [data, includeSystemEngine, getAccount, enabledGcpAccount]);

  return { data: engines, isLoading, error };
};
